// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Tab state synced to a URL query param (default `?tab=`), so a refresh,
 * the back button, or a shared link round-trips the active tab on a
 * detail page. Used by the customer / prospect detail pages.
 *
 * The active tab is *derived* from the param (no local state) — an
 * unknown or missing value falls back to `defaultId`. Switching tabs
 * uses history `replace`, so Back returns to the list rather than
 * walking through every tab the user clicked. The param is omitted when
 * it equals `defaultId` to keep the landing URL clean.
 *
 * @param validIds  the allowed tab ids (anything else → `defaultId`)
 * @param defaultId the tab shown when the param is missing/invalid
 * @param paramName the query string key (default `"tab"`)
 */
export function useTabParam(
  validIds: readonly string[],
  defaultId: string,
  paramName = "tab",
): [string, (id: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(paramName);
  const active = raw && validIds.includes(raw) ? raw : defaultId;

  const setActiveTab = useCallback(
    (id: string): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id === defaultId) next.delete(paramName);
          else next.set(paramName, id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaultId, paramName],
  );

  return [active, setActiveTab];
}
